import { Box, Group, Stack, Text } from '@mantine/core'
import { PauseIcon, SealCheckIcon } from '@phosphor-icons/react'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import type { BoggleGameModeDefinition } from '~/boggle/gameModes'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import type { BoggleRunSummaryScore } from '~/boggle/config/runSummaryScore'
import { isBoggleAchievementsPlayerUiEnabled } from '~/boggle/featureFlags'
import type { RunSummaryAchievementEvent } from '~/boggle/lib/achievements/runSummaryAchievementEvents'
import {
  FloatingAccumulatingScoreTag,
  FloatingScoreTag,
} from '~/boggle/ui/atoms/FloatingScoreTag'
import { IconButton } from '~/boggle/ui/atoms/Buttons'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import { BOGGLE_RUN_SEED_BOTTOM_INSET_PX } from '~/boggle/ui/chrome/AppLayout'
import { CompendiumTrophyButton } from '~/boggle/ui/compendium/CompendiumTrophyButton'
import { GameModeHowToPlayModal } from '~/boggle/ui/modes/GameModeHowToPlayModal'
import { BoardFrame } from '~/boggle/ui/run/BoardFrame'
import { GoldTilesRunTracker } from '~/boggle/ui/run/GoldTilesRunTracker'
import { RunHud } from '~/boggle/ui/run/RunHud'
import { RunMeterIsland } from '~/boggle/ui/run/RunMeterIsland'
import { RunTimerIsland } from '~/boggle/ui/run/RunTimerIsland'
import {
  preloadRunResultsRatingBadgeImages,
  RunResultsRatingBadge,
} from '~/boggle/ui/run/RunResultsRatingBadge'
import {
  RunSummary,
  RUN_SUMMARY_METRIC_RAMP_MS,
  type BoggleChallengeSummaryOutcome,
  type BoggleStandardSummaryOutcome,
} from '~/boggle/ui/run/RunSummary'
import {
  hasActiveBoggleScoreFeedback,
  type BoggleScoreFeedbackState,
  useBoggleComboFeedback,
  useBoggleTimerBonusFeedback,
} from '~/boggle/ui/run/ScoreFeedback'
import {
  useRunResultsChoreography,
  type RunResultsTagAnchor,
} from '~/boggle/ui/run/useRunResultsChoreography'
import { useBoggleModeSession } from '~/boggle/ui/state/ModeSession'
import {
  BOGGLE_GOLD_RUSH_BURST_ID,
  isTimerEndGoldRushBurstConfigured,
} from '~/boggle/lib/meterBursts'
import type { BoggleStore } from '~/boggle/ui/state/Store'
import {
  useBoggleIntervalPrepActive,
  useBoggleMeterGoldRushActive,
  useBogglePlayingPhasePaused,
} from '~/boggle/ui/state/Store'

/** Sequencing for the gameplay→results crossfade. Kept in sync with `RunHud`. */
const PLAY_FADE_OUT_MS = 300
const RESULTS_FADE_IN_MS = 400
const RESULTS_FADE_IN_DELAY_MS = PLAY_FADE_OUT_MS

/** When Skip appears after the play→results crossfade (`RunSummary`). */
export const RUN_SUMMARY_SKIP_BUTTON_REVEAL_DELAY_MS = 1000

/**
 * Pause overlay wiring for a particular run variant (standard / challenge / practice).
 * The surface is variant-agnostic; the parent owns these callbacks + labels.
 */
export type BoggleRunPauseConfig = {
  /** Returns to the upstream challenge staging/reveal screen (challenge + practice). */
  onBackToChallenge?: () => void
  /** Ends the run entirely (standard + playground); only used when `onBackToChallenge` is absent. */
  onEndRun?: () => void
  /** Label for the secondary "exit" affordance in the pause overlay. */
  endRunLabel?: string
}

/** Summary content passed in by the run variant; null/undefined fields are skipped. */
export type BoggleRunSummaryProps = {
  achievementEvents?: readonly RunSummaryAchievementEvent[] | null
  challengeOutcome?: BoggleChallengeSummaryOutcome | null
  /** Action buttons rendered above the back-link on the summary screen. */
  actions?: {
    label: string
    onClick: () => void
    tone?: 'challenge' | 'cta' | 'neutral' | 'warm'
  }[]
  onBack: () => void
  onNewGame: () => void
  standardSummaryOutcome?: BoggleStandardSummaryOutcome | null
  summaryScore: BoggleRunSummaryScore
}

function RunSummaryAchievementToast({
  event,
}: {
  event: RunSummaryAchievementEvent | null
}) {
  const visible = event !== null
  const eyebrow =
    event?.type === 'rating_revealed' ? 'Rating Revealed' : 'Achievement Unlocked'
  const title =
    event?.type === 'achievement_unlocked'
      ? `${event.title}: ${event.tierLabel}`
      : event?.title

  return (
    <Box
      aria-live="polite"
      bottom={118}
      left="50%"
      pos="absolute"
      style={{
        opacity: visible ? 1 : 0,
        pointerEvents: 'none',
        transform: visible
          ? 'translate(-50%, 0) scale(1)'
          : 'translate(-50%, 8px) scale(0.98)',
        transition:
          'opacity 180ms ease-out, transform 220ms cubic-bezier(0.16, 1, 0.3, 1)',
        zIndex: 7,
      }}
      w="min(360px, calc(100% - 32px))"
    >
      {event ? (
        <Stack
          bg="rgba(19, 22, 28, 0.94)"
          bd="1px solid rgba(255, 255, 255, 0.18)"
          bdrs={8}
          gap={4}
          px={14}
          py={12}
          style={{
            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.28)',
          }}
        >
          <Group
            c={event.type === 'rating_revealed' ? '#f0c85a' : '#a7f3d0'}
            fz={12}
            fw={700}
            gap={6}
            style={{
              letterSpacing: 0,
              lineHeight: 1,
              textTransform: 'uppercase',
            }}
          >
            <IconGlyph icon={SealCheckIcon} size={15} weight="fill" />
            {eyebrow}
          </Group>
          <Text c="white" fz={18} fw={700} lh={1.15}>
            {title}
          </Text>
          <Text c="gray.3" fz={13} lh={1.25}>
            {event.description}
          </Text>
        </Stack>
      ) : null}
    </Box>
  )
}

function CentralPanel({ children }: { children: ReactNode }) {
  return (
    <Box
      h="100%"
      mx="auto"
      pos="relative"
      pb={10}
      px={20}
      w="100%"
      maw={600}
      style={{
        overflow: 'visible',
      }}
    >
      {children}
    </Box>
  )
}

function RunHudWithLiveTimer({
  bufferedRunState,
  comboFeedbackEvents,
  reserveRunLayout,
  resultsInstantScoreRamp,
  resultsScore,
  resultsScoreMaxValueVisual,
  scoreFeedback,
  showEndBuffer,
  showResultsPhase,
  state,
  store,
  timerBonusFeedbackEvents,
}: {
  bufferedRunState: BoggleGameState
  comboFeedbackEvents: ReturnType<typeof useBoggleComboFeedback>
  reserveRunLayout: boolean
  resultsInstantScoreRamp: boolean
  resultsScore: number
  resultsScoreMaxValueVisual: number
  scoreFeedback: BoggleScoreFeedbackState
  showEndBuffer: boolean
  showResultsPhase: boolean
  state: BoggleGameState
  store: BoggleStore
  timerBonusFeedbackEvents: ReturnType<typeof useBoggleTimerBonusFeedback>
}) {
  const liveMeterGoldRushActive = useBoggleMeterGoldRushActive(store)
  const livePrepActive = useBoggleIntervalPrepActive(store, state.intervalPlayBeginsAtMs)

  const hudState = showEndBuffer ? bufferedRunState : state
  const timerIntervalCount = hudState.playgroundSettings.intervalCount
  const timerIntervalDurationMs = hudState.intervalDurationMs
  const timerIntervalsRemaining = hudState.intervalsRemaining
  const meterGoldRushActive = showEndBuffer
    ? bufferedRunState.meter.burstActive &&
      bufferedRunState.meter.activeBurstId === BOGGLE_GOLD_RUSH_BURST_ID
    : liveMeterGoldRushActive
  const showGoldRushTimerLabel =
    reserveRunLayout &&
    meterGoldRushActive &&
    isTimerEndGoldRushBurstConfigured(state.playgroundSettings.meter)
  const prepBlocksTimerGauge = showEndBuffer
    ? bufferedRunState.intervalPlayBeginsAtMs !== null &&
      bufferedRunState.timeMs < bufferedRunState.intervalPlayBeginsAtMs
    : livePrepActive
  const showTimerGauge =
    reserveRunLayout &&
    !showGoldRushTimerLabel &&
    timerIntervalCount !== null &&
    timerIntervalDurationMs !== null &&
    !prepBlocksTimerGauge
  const showIntervalCounter =
    reserveRunLayout && timerIntervalCount !== null && timerIntervalCount > 1
  const showComboCounter = reserveRunLayout && state.playgroundSettings.combo.enabled
  const showMeterCounter = reserveRunLayout && state.playgroundSettings.meter.enabled
  const showMeterGauge =
    showMeterCounter &&
    (showEndBuffer
      ? bufferedRunState.intervalPlayBeginsAtMs === null ||
        bufferedRunState.timeMs >= bufferedRunState.intervalPlayBeginsAtMs
      : !livePrepActive)
  const intervalCounterIndex =
    timerIntervalCount === null
      ? null
      : timerIntervalsRemaining === null
        ? timerIntervalCount
        : Math.min(timerIntervalCount, timerIntervalCount - timerIntervalsRemaining + 1)
  const isLastInterval =
    timerIntervalCount !== null &&
    intervalCounterIndex !== null &&
    intervalCounterIndex === timerIntervalCount
  const meterHudPhasePlaying = showEndBuffer
    ? bufferedRunState.phase === 'playing'
    : state.phase === 'playing'

  const timerSlot =
    reserveRunLayout || showGoldRushTimerLabel || showTimerGauge ? (
      <RunTimerIsland
        bufferedRunState={bufferedRunState}
        reserveRunLayout={reserveRunLayout}
        showEndBuffer={showEndBuffer}
        showGoldRushTimerLabel={showGoldRushTimerLabel}
        showMeterGaugeSibling={showMeterGauge}
        showTimerGauge={showTimerGauge}
        store={store}
        timerBonusFeedbackEvents={timerBonusFeedbackEvents}
        timerIntervalCount={timerIntervalCount}
        timerIntervalDurationMs={timerIntervalDurationMs}
        timerIntervalsRemaining={timerIntervalsRemaining}
      />
    ) : null

  const meterSlot =
    showMeterGauge || showMeterCounter ? (
      <RunMeterIsland
        bufferedRunState={bufferedRunState}
        meterHudPhasePlaying={meterHudPhasePlaying}
        meterSettings={state.playgroundSettings.meter}
        showEndBuffer={showEndBuffer}
        showMeterCounter={showMeterCounter}
        showMeterGauge={showMeterGauge}
        store={store}
      />
    ) : null

  return (
    <RunHud
      combo={state.combo}
      comboFeedbackEvents={comboFeedbackEvents}
      intervalCounterIndex={intervalCounterIndex}
      isLastInterval={isLastInterval}
      meterSlot={meterSlot}
      reserveRunLayout={reserveRunLayout}
      resultsInstantScoreRamp={resultsInstantScoreRamp}
      resultsScore={resultsScore}
      resultsScoreMaxValueVisual={resultsScoreMaxValueVisual}
      scoreFeedback={scoreFeedback}
      showComboCounter={showComboCounter}
      showEndedSummary={false}
      showIntervalCounter={showIntervalCounter}
      showResultsPhase={showResultsPhase}
      timerIntervalCount={timerIntervalCount}
      timerSlot={timerSlot}
      tilesCleared={state.tilesCleared}
    />
  )
}

/**
 * Top chrome slot — slides in from above (translateY + opacity) when the run
 * enters the results phase, holds layout space (mih=56) at all times so the
 * HUD below never shifts.
 */
function RunSurfaceTopChrome({
  children,
  showPausePhase,
  showResultsPhase,
}: {
  children: ReactNode
  /** Best-score / challenge tray chrome while paused (same slot as results). */
  showPausePhase: boolean
  showResultsPhase: boolean
}) {
  const visible = showResultsPhase || showPausePhase
  const transition = showResultsPhase
    ? `transform ${RESULTS_FADE_IN_MS}ms cubic-bezier(0.16, 1, 0.3, 1) ${RESULTS_FADE_IN_DELAY_MS}ms, ` +
      `opacity 200ms ease-out ${RESULTS_FADE_IN_DELAY_MS}ms`
    : showPausePhase
      ? 'transform 200ms ease-out 0ms, opacity 200ms ease-out 0ms'
      : 'transform 200ms ease-in 0ms, opacity 200ms ease-out 0ms'

  return (
    <Box
      mih={56}
      style={{
        flex: '0 0 auto',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? undefined : 'none',
        transform: visible ? 'translateY(0)' : 'translateY(-100%)',
        transition,
      }}
    >
      {children}
    </Box>
  )
}

/**
 * Unified run surface — owns the persistent layout (gold tracker, HUD, central area)
 * and crossfades between the play sub-tree and the results sub-tree based on game phase.
 * Both bodies stay mounted across the transition so persistent elements (Tiles Cleared,
 * Gold Tracker) never remount or jump.
 *
 * Variant-specific logic (storage, outcome calc, navigation, top chrome) lives in the
 * parent variant surface (`StandardRunSurface`, `ChallengeRunSurface`, …).
 */
export function RunSurface({
  accentColor,
  gameRulesDefinition,
  pauseConfig,
  onRestart,
  scoreFeedback,
  showFloatingPauseButton = true,
  state,
  store,
  summary,
  topChrome,
}: {
  accentColor?: string
  gameRulesDefinition?: BoggleGameModeDefinition
  pauseConfig?: BoggleRunPauseConfig
  onRestart: () => void
  scoreFeedback: BoggleScoreFeedbackState
  showFloatingPauseButton?: boolean
  state: BoggleGameState
  store: BoggleStore
  /** Summary data + callbacks supplied by the variant; required to render the results sub-tree. */
  summary: BoggleRunSummaryProps
  /** Persistent top-of-surface chrome (Best Score badge / Challenge tray / playground label). */
  topChrome?: ReactNode
}) {
  const { isDevMode } = useBoggleModeSession()
  const achievementsPlayerUiEnabled = isBoggleAchievementsPlayerUiEnabled(isDevMode)
  const comboFeedbackEvents = useBoggleComboFeedback(store)
  const timerBonusFeedbackEvents = useBoggleTimerBonusFeedback(store)
  const isPaused = useBogglePlayingPhasePaused(store, state.phase)
  const hasActiveScoreFeedback = hasActiveBoggleScoreFeedback(scoreFeedback)
  const showEndBuffer = state.phase === 'ended' && hasActiveScoreFeedback
  const showResults = state.phase === 'ended' && !hasActiveScoreFeedback
  const reserveRunLayout = state.phase === 'playing' || showEndBuffer
  const lastPlayableStateRef = useRef(state)
  const bufferedRunState = showEndBuffer ? lastPlayableStateRef.current : state
  const [gameRulesModalOpen, setGameRulesModalOpen] = useState(false)
  const openGameRules = useCallback(() => {
    setGameRulesModalOpen(true)
  }, [])
  const closeGameRules = useCallback(() => {
    setGameRulesModalOpen(false)
  }, [])
  const gameRulesAction = gameRulesDefinition ? openGameRules : undefined

  useEffect(() => {
    if (state.phase === 'playing') {
      lastPlayableStateRef.current = state
    }
  }, [state])

  useEffect(() => {
    preloadRunResultsRatingBadgeImages()
  }, [])

  /** Anchor refs for the tier `FloatingScoreTag` flight paths. */
  const tierValueRefs = useRef<(HTMLDivElement | null)[]>([])
  /** Anchor refs for the badge reveal medallions (upper-right corner of each). */
  const badgeAnchorsRef = useRef<(HTMLDivElement | null)[]>([])

  const visibleAchievementEvents = achievementsPlayerUiEnabled
    ? summary.achievementEvents
    : []

  const choreography = useRunResultsChoreography({
    achievementEvents: visibleAchievementEvents,
    isActive: showResults,
    summaryScore: summary.summaryScore,
  })

  const [skipButtonReveal, setSkipButtonReveal] = useState(false)
  useEffect(() => {
    if (!showResults) {
      setSkipButtonReveal(false)
      return
    }
    const handle = window.setTimeout(() => {
      setSkipButtonReveal(true)
    }, RUN_SUMMARY_SKIP_BUTTON_REVEAL_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [showResults])

  const showSkipButton =
    skipButtonReveal &&
    !choreography.skippedToSummaryEnd &&
    !choreography.resultsScoringAnimationsComplete
  const tierSummaryMetricRampMs = choreography.skippedToSummaryEnd
    ? 0
    : RUN_SUMMARY_METRIC_RAMP_MS

  const resolveTagAnchor = (
    anchor: RunResultsTagAnchor,
  ): RefObject<HTMLElement | null> => {
    /** Tier/badge value refs are slots in a refs array — return a derived ref that reads the slot on access. */
    const slots = anchor.kind === 'badge' ? badgeAnchorsRef : tierValueRefs
    return {
      get current() {
        return slots.current[anchor.index] ?? null
      },
    } as RefObject<HTMLElement | null>
  }

  /** Cross-fade transitions for play-side UI (board, controls, floating pause). */
  const playBodyStyle = {
    opacity: showResults ? 0 : 1,
    pointerEvents: showResults ? ('none' as const) : undefined,
    transition: showResults
      ? `opacity ${PLAY_FADE_OUT_MS}ms ease-out 0ms`
      : `opacity ${RESULTS_FADE_IN_MS}ms ease-out ${RESULTS_FADE_IN_DELAY_MS}ms`,
  } as const

  /** Cross-fade transitions for the results body (tier reveal, rating, actions). */
  const resultsBodyStyle = {
    opacity: showResults ? 1 : 0,
    pointerEvents: showResults ? undefined : ('none' as const),
    transition: showResults
      ? `opacity ${RESULTS_FADE_IN_MS}ms ease-out ${RESULTS_FADE_IN_DELAY_MS}ms`
      : `opacity ${PLAY_FADE_OUT_MS}ms ease-out 0ms`,
  } as const

  const showPauseButton =
    showFloatingPauseButton && state.phase === 'playing' && !isPaused

  return (
    <Box
      component="section"
      h="100%"
      mih={0}
      pos="relative"
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <RunSurfaceTopChrome showPausePhase={isPaused} showResultsPhase={showResults}>
        {topChrome}
      </RunSurfaceTopChrome>

      {/*
        Top-left corner — persistent across the play→results crossfade. During play
        the gold tracker lives here; once it drains + hides, the rating badge pops
        into the same slot. Both share the corner real estate without overlapping
        in time.
      */}
      <Box
        pos="absolute"
        left={4}
        top={0}
        style={{
          overflow: 'visible',
          pointerEvents: 'none',
          zIndex: 6,
        }}
      >
        {state.goldTilesUsed > 0 ? (
          <GoldTilesRunTracker
            drainAnimation={choreography.goldDrain}
            forceHidden={choreography.skippedToSummaryEnd}
            goldTilesUsed={state.goldTilesUsed}
          />
        ) : null}
        <Box ml={10} mt={26} style={{ transform: 'rotateZ(-7deg)' }}>
          <RunResultsRatingBadge
            rating={summary.summaryScore.rating}
            showRating={choreography.showRating}
          />
        </Box>
      </Box>

      <GameModeHowToPlayModal
        definition={
          gameRulesModalOpen && gameRulesDefinition ? gameRulesDefinition : null
        }
        onClose={closeGameRules}
      />

      {showResults && choreography.summaryActionsRevealReady ? (
        <Box
          pos="absolute"
          right={14}
          top={14}
          style={{
            zIndex: 6,
          }}
        >
          <CompendiumTrophyButton testId="boggle-results-compendium-trophy" />
        </Box>
      ) : null}

      <Box flex={1} mih={0} pos="relative">
        <CentralPanel>
          <Stack justify="space-evenly" gap={0} h="100%" mih={0}>
            <RunHudWithLiveTimer
              bufferedRunState={bufferedRunState}
              comboFeedbackEvents={comboFeedbackEvents}
              reserveRunLayout={reserveRunLayout}
              resultsInstantScoreRamp={choreography.skippedToSummaryEnd}
              resultsScore={choreography.score}
              resultsScoreMaxValueVisual={Math.max(1, summary.summaryScore.total)}
              scoreFeedback={scoreFeedback}
              showEndBuffer={showEndBuffer}
              showResultsPhase={showResults}
              state={state}
              store={store}
              timerBonusFeedbackEvents={timerBonusFeedbackEvents}
            />
            <Box flex={1} mih={0} pos="relative">
              {/* Play body — board + selection + controls + pause overlay. Fades out on results. */}
              <Box
                pos="absolute"
                inset={0}
                style={{ display: 'flex', flexDirection: 'column', ...playBodyStyle }}
              >
                <BoardFrame
                  accentColor={accentColor}
                  playAccentColor={gameRulesDefinition?.color ?? accentColor}
                  bufferedRunState={bufferedRunState}
                  isPaused={isPaused}
                  onBackToChallengeFromPause={pauseConfig?.onBackToChallenge}
                  onEndRunFromPause={pauseConfig?.onEndRun}
                  onGameRules={gameRulesAction}
                  pauseOverlayEndRunLabel={pauseConfig?.endRunLabel}
                  onRestart={onRestart}
                  showEndBuffer={showEndBuffer}
                  state={state}
                  store={store}
                />
              </Box>
              {/* Results body — tier reveal + rating + actions. Fades in after play fades out. */}
              <Box pos="absolute" inset={0} style={resultsBodyStyle}>
                <RunSummary
                  accentColor={accentColor}
                  actions={summary.actions}
                  badgeAnchorsRef={badgeAnchorsRef}
                  badgeRevealPhases={choreography.badgeRevealPhases}
                  challengeOutcome={summary.challengeOutcome}
                  disablePrimarySummaryActions={!choreography.summaryActionsRevealReady}
                  onBack={summary.onBack}
                  onNewGame={summary.onNewGame}
                  revealedTierCount={choreography.revealedTierCount}
                  showOutcomeCopy={choreography.showOutcomeCopy}
                  showSkipButton={showSkipButton}
                  skipSummaryScoringAnimations={choreography.skipSummaryScoringAnimations}
                  skippedSummaryScoringAnimations={choreography.skippedToSummaryEnd}
                  summaryScore={summary.summaryScore}
                  standardSummaryOutcome={summary.standardSummaryOutcome}
                  tierMetricRampDurationMs={tierSummaryMetricRampMs}
                  tierValueRefs={tierValueRefs}
                />
              </Box>
            </Box>
          </Stack>
        </CentralPanel>
      </Box>

      {showFloatingPauseButton ? (
        <Box
          bottom={BOGGLE_RUN_SEED_BOTTOM_INSET_PX}
          left="50%"
          pos="absolute"
          style={{
            transform: 'translateX(-50%)',
            zIndex: 5,
            opacity: showPauseButton ? 1 : 0,
            pointerEvents: showPauseButton ? undefined : 'none',
            transition: `opacity ${PLAY_FADE_OUT_MS}ms ease-out`,
          }}
        >
          <IconButton onClick={() => store.togglePaused()}>
            <IconGlyph icon={PauseIcon} size={24} weight="fill" />
          </IconButton>
        </Box>
      ) : null}

      {achievementsPlayerUiEnabled ? (
        <RunSummaryAchievementToast event={choreography.activeAchievementEvent} />
      ) : null}

      {choreography.tierActiveTag ? (
        <FloatingScoreTag
          anchorRef={resolveTagAnchor(choreography.tierActiveTag.anchor)}
          durationMs={choreography.tierActiveTag.durationMs}
          key={choreography.tierActiveTag.key}
          preDrainDelayMs={choreography.tierActiveTag.preDrainDelayMs}
          startValue={choreography.tierActiveTag.startValue}
        />
      ) : null}
      {choreography.badgeScoreTag ? (
        <FloatingAccumulatingScoreTag
          anchorRef={resolveTagAnchor(choreography.badgeScoreTag.anchor)}
          cumulativeValue={choreography.badgeScoreTag.cumulativeValue}
          finalDrain={choreography.badgeScoreTag.finalDrain}
          jiggleNonce={choreography.badgeScoreTag.jiggleNonce}
          onDrainComplete={choreography.clearBadgeFloatingTag}
        />
      ) : null}
    </Box>
  )
}
